import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuCalendar,
  LuCalendarCheck,
  LuCheck,
  LuCircleDollarSign,
  LuClock,
  LuHandshake,
  LuNetwork,
  LuShieldCheck,
  LuTarget,
  LuTrendingUp,
  LuTrophy,
  LuUser,
  LuUsers,
} from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import PipelineTalentDashboard from '../app/components/PipelineTalentDashboard';
import StaggeredScrollReveal from '../app/components/StaggeredScrollReveal';
import StepProcessFlow from '../app/components/StepProcessFlow';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';

const heroFeatures: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'Proactive, not reactive.',
    body: 'Build pipelines before you have to hire.',
    Icon: LuTarget,
  },
  {
    title: 'Always ready.',
    body: 'Access qualified talent when opportunity or change arises.',
    Icon: LuNetwork,
  },
  {
    title: 'Lower risk. Higher impact.',
    body: 'Stronger talent bench. Better business outcomes.',
    Icon: LuShieldCheck,
  },
];

type ProcessColor = 'teal' | 'blue' | 'purple';

const pipeliningProcessSteps: {
  n: string;
  title: string;
  body: string;
  Icon: IconType;
  color: ProcessColor;
}[] = [
  {
    n: '1',
    title: 'Understand',
    body: 'We align with your business objectives, role roadmap, and ideal candidate profile.',
    Icon: LuTarget,
    color: 'teal',
  },
  {
    n: '2',
    title: 'Map & Source',
    body: 'We identify and engage passive, high-potential talent in the market.',
    Icon: LuUsers,
    color: 'blue',
  },
  {
    n: '3',
    title: 'Engage & Nurture',
    body: 'We build relationships over time to keep talent engaged and informed.',
    Icon: LuHandshake,
    color: 'purple',
  },
  {
    n: '4',
    title: 'Qualify & Prepare',
    body: 'We continuously assess, validate and prepare talent for your opportunities.',
    Icon: LuCalendarCheck,
    color: 'teal',
  },
  {
    n: '5',
    title: 'Deliver on Demand',
    body: "When the time is right, you'll have a shortlist of qualified, ready-to-go talent.",
    Icon: LuUser,
    color: 'blue',
  },
];

const pipelineDifferentItems = [
  'Curated talent pools tailored to your needs',
  'Continuous engagement keeps top talent warm',
  'Real-time visibility into your pipeline health',
  'Flexibility to scale up or down as your needs change',
  'Candidates are exclusive only to your pipeline/org.',
  'Candidates are pre-cleared for your company, culture, and opportunities',
  'Dedicated pipeline management leader',
];

const whoItsForItems: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'High-Growth Companies',
    body: 'Scale faster with a steady pipeline of top talent.',
    Icon: LuTrendingUp,
  },
  {
    title: 'Companies in Transition',
    body: 'Navigate change with the right people in place.',
    Icon: LuNetwork,
  },
  {
    title: 'Hard-to-Fill Roles',
    body: 'Build a bench of qualified candidates for niche or critical roles.',
    Icon: LuCalendarCheck,
  },
  {
    title: 'Long-Term Strategic Hiring',
    body: 'Invest in future-ready talent for lasting impact.',
    Icon: LuShieldCheck,
  },
];

type PipelineBenefitIconTone = 'teal' | 'blue';

const pipelineBenefits: {
  Icon: IconType;
  iconTone: PipelineBenefitIconTone;
  variant: 'stat' | 'title';
  stat?: string;
  title: string;
  body: string;
}[] = [
  {
    variant: 'stat',
    stat: '30–50%',
    title: 'Faster Time-to-Hire',
    body: 'Engaged, pre-qualified talent reduces hiring cycle time.',
    Icon: LuClock,
    iconTone: 'teal',
  },
  {
    variant: 'stat',
    stat: '20–30%',
    title: 'Lower Cost per Hire',
    body: 'Proactive pipelines reduce the cost and effort of urgent hiring.',
    Icon: LuCircleDollarSign,
    iconTone: 'blue',
  },
  {
    variant: 'title',
    title: 'Higher Quality',
    body: 'Access to top-tier, passive talent improves quality of hire.',
    Icon: LuTrophy,
    iconTone: 'teal',
  },
  {
    variant: 'title',
    title: 'Stronger Retention',
    body: 'Better alignment and engagement lead to greater long-term retention.',
    Icon: LuUsers,
    iconTone: 'blue',
  },
];

const pipeliningProcessStepsHome = pipeliningProcessSteps.map(({ title, body, Icon }) => ({
  title,
  body,
  Icon,
}));

export default function PipeliningPage() {
  return (
    <>
      <section className="pl-hero hero-pattern text-white">
        <div className="pl-hero__main">
          <Container className="pl-hero__container">
            <Breadcrumb className="solution-hero-breadcrumb pl-hero__breadcrumb mb-4">
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
                Solutions
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Pipelining Talent Infrastructure</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="pl-hero__row align-items-stretch gy-4">
              <Col lg={6} className="pl-hero__copy-col d-flex flex-column">
                <HeroAnimated className="pl-hero__animated">
                  <div className="pl-hero__headline">
                    <h1 data-hero-item className="display-5 fw-bold mb-0">
                      Pipelining Talent{' '}
                      <span className="text-gradient-teal-blue">Infrastructure.</span>
                    </h1>
                  </div>

                  <div className="pl-hero__body">
                    <p data-hero-item className="lead text-white-50 mb-0 pe-lg-2">
                      A proactive approach to building a sustainable pipeline of high-quality talent, so
                      you&apos;re always ready for what&apos;s next.
                    </p>
                  </div>

                  <div className="pl-hero__dashboard pl-hero__dashboard--mobile d-lg-none">
                    <PipelineTalentDashboard />
                  </div>

                  <Row data-hero-item className="pl-hero__features g-3 mt-auto">
                    {heroFeatures.map((f) => (
                      <Col sm={4} key={f.title}>
                        <div className="pl-hero-feature">
                          <div className="pl-hero-feature__icon">
                            <SbrIcon icon={f.Icon} size={22} />
                          </div>
                          <div className="pl-hero-feature__copy">
                            <div className="pl-hero-feature__title">{f.title}</div>
                            <p className="pl-hero-feature__body">{f.body}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </HeroAnimated>
              </Col>

              <Col lg={6} className="pl-hero__dashboard-col d-none d-lg-flex">
                <RevealSection className="h-100 w-100">
                  <PipelineTalentDashboard />
                </RevealSection>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="bg-white py-5 py-lg-6 border-top border-secondary border-opacity-10">
        <Container>
          <RevealSection>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-3">Our Pipelining Process</h2>
              <p className="sbr-section__lead home-section-lead mb-0 mx-auto">
                We build and nurture talent pipelines aligned to your business goals, so you have
                the right people ready when you need them most.
              </p>
            </div>
          </RevealSection>
          <StepProcessFlow variant="home" steps={pipeliningProcessStepsHome} />
        </Container>
      </section>

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <Row className="gy-4 align-items-stretch cs-benefits-row pl-benefits-row">
              <Col lg={6} className="d-flex">
                <div className="cs-why-choose w-100">
                  <h2 className="cs-why-choose__title sbr-card__title mb-4">
                    What Makes Our Pipelines Different
                  </h2>
                  <ul className="cs-why-choose__list list-unstyled mb-0">
                    {pipelineDifferentItems.map((t) => (
                      <li key={t} className="cs-why-choose__item">
                        <span className="cs-why-choose__check" aria-hidden>
                          <SbrIcon icon={LuCheck} size={14} />
                        </span>
                        <span className="cs-why-choose__text">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
              <Col lg={6} className="d-flex">
                <div className="cs-ideal-for w-100">
                  <h2 className="cs-ideal-for__title sbr-card__title mb-4">Who It&apos;s For</h2>
                  <StaggeredScrollReveal className="cs-ideal-for__list">
                    {whoItsForItems.map((x) => (
                      <div key={x.title} className="cs-ideal-for__row" data-stagger-item>
                        <div className="cs-ideal-for__icon" aria-hidden>
                          <SbrIcon icon={x.Icon} size={22} />
                        </div>
                        <div className="cs-ideal-for__copy">
                          <div className="cs-ideal-for__heading">{x.title}</div>
                          <p className="cs-ideal-for__body mb-0">{x.body}</p>
                        </div>
                      </div>
                    ))}
                  </StaggeredScrollReveal>
                </div>
              </Col>
            </Row>
          </Container>
        </RevealSection>
      </section>

      <section className="bg-white py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-0">
                The Benefits of a Strong Talent Pipeline
              </h2>
            </div>
            <div className="pl-benefits">
              {pipelineBenefits.map((b, index) => (
                <Fragment key={b.title}>
                  {index > 0 && <div className="pl-benefits__divider" aria-hidden />}
                  <div className={`pl-benefits__item pl-benefits__item--${b.iconTone}`}>
                    <div className="pl-benefits__icon" aria-hidden>
                      <SbrIcon icon={b.Icon} size={26} />
                    </div>
                    <div className="pl-benefits__copy">
                      {b.variant === 'stat' ? (
                        <>
                          <div className="pl-benefits__stat">{b.stat}</div>
                          <div className="pl-benefits__heading">{b.title}</div>
                        </>
                      ) : (
                        <div className="pl-benefits__heading pl-benefits__heading--solo">{b.title}</div>
                      )}
                      <p className="pl-benefits__body mb-0">{b.body}</p>
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <>
            <div>Build your future-ready talent pipeline today.</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-1 mb-0">
              Let&apos;s create a proactive talent strategy that drives growth.
            </div>
          </>
        }
      />
    </>
  );
}
